import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Smartphone } from "lucide-react";

interface OrientationLockProps {
    children: React.ReactNode;
    enabled?: boolean;
}

const OrientationLock: React.FC<OrientationLockProps> = ({ 
    children, 
    enabled = true 
}) => {
    const [isLandscape, setIsLandscape] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [isMobileDevice, setIsMobileDevice] = useState(false);

    useEffect(() => {
        if (!enabled) return;

        // Check if device is mobile/tablet (width <= 1024px)
        const checkDeviceType = () => {
            const isMobile = window.innerWidth <= 1024;
            setIsMobileDevice(isMobile);
        };

        // Check initial orientation
        const checkOrientation = () => {
            const isLandscapeMode = window.innerWidth > window.innerHeight;
            setIsLandscape(isLandscapeMode);
            // Only show popup on mobile devices in landscape
            setShowPopup(isLandscapeMode && isMobileDevice);
        };

        // Check on mount
        checkDeviceType();
        checkOrientation();

        // Listen for orientation changes
        const handleOrientationChange = () => {
            setTimeout(() => {
                checkDeviceType();
                checkOrientation();
            }, 100); // Small delay to ensure accurate measurement
        };

        // Listen for resize events (for desktop browsers)
        const handleResize = () => {
            checkDeviceType();
            checkOrientation();
        };

        // Add event listeners
        window.addEventListener("orientationchange", handleOrientationChange);
        window.addEventListener("resize", handleResize);

        // Try to lock orientation to portrait (only on mobile browsers)
        const lockOrientation = async () => {
            // Only lock orientation on mobile devices
            if (!isMobileDevice) return;
            
            try {
                // Check if screen orientation API is available
                if (screen.orientation && (screen.orientation as any).lock) {
                    await (screen.orientation as any).lock("portrait");
                }
                // For older browsers, try the deprecated API
                else if ((screen as any).lockOrientation) {
                    (screen as any).lockOrientation("portrait");
                }
                // For iOS Safari
                else if ((screen as any).orientation && (screen as any).orientation.lock) {
                    await (screen as any).orientation.lock("portrait");
                }
            } catch (error) {
        
            }
        };

        // Attempt to lock orientation (only on mobile devices)
        if (isMobileDevice) {
            lockOrientation();
        }

        // Cleanup
        return () => {
            window.removeEventListener("orientationchange", handleOrientationChange);
            window.removeEventListener("resize", handleResize);
        };
    }, [enabled]);

    // If not enabled, just render children
    if (!enabled) {
        return <>{children}</>;
    }

    // If not a mobile device, always show content regardless of orientation
    if (!isMobileDevice) {
        return <>{children}</>;
    }

    return (
        <>
            {/* Main content - only hidden on mobile devices in landscape */}
            <div className={`${isLandscape ? "hidden" : "block"}`}>
                {children}
            </div>

            {/* Landscape popup */}
            <AnimatePresence>
                {showPopup && isLandscape && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
                        style={{ 
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9999
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="bg-white rounded-2xl p-8 mx-4 max-w-sm text-center shadow-2xl"
                        >
                            {/* Phone icon with rotation animation */}
                            <div className="flex justify-center mb-6">
                                <motion.div
                                    animate={{ rotate: [0, -90, 0] }}
                                    transition={{ 
                                        duration: 2, 
                                        repeat: Infinity, 
                                        ease: "easeInOut" 
                                    }}
                                    className="relative"
                                >
                                    <Smartphone className="h-16 w-16 text-blue-600" />
                                    <RotateCcw className="h-6 w-6 text-red-500 absolute -top-2 -right-2" />
                                </motion.div>
                            </div>

                            {/* Message */}
                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                Please Rotate Your Device
                            </h2>
                            <p className="text-gray-600 mb-6">
                                This app works best in portrait mode. Please rotate your device to continue.
                            </p>

                            {/* Instructions */}
                            <div className="bg-blue-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-blue-800 font-medium">
                                    💡 Tip: Turn your device to portrait mode (upright position)
                                </p>
                            </div>

                            {/* Visual indicator */}
                            <div className="flex justify-center">
                                <div className="w-12 h-16 border-2 border-gray-300 rounded-lg flex items-center justify-center bg-gray-100">
                                    <div className="w-1 h-8 bg-blue-600 rounded-full"></div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default OrientationLock;
